import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, Gauge, Award, Camera } from "lucide-react";
import api from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { HighlightsWall } from "@/components/HighlightsWall";

const INTERIOR = "https://customer-assets.emergentagent.com/job_garage-modern-2/artifacts/q4tbz2bf_IMG_8276.png";
const PRECISION = "https://customer-assets.emergentagent.com/job_garage-modern-2/artifacts/vqz25ua7_IMG_8275.png";

export default function AboutPage() {
  const [gallery, setGallery] = useState([]);
  const { siteSettings } = useTheme() || {};
  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);
  const apiBase = (api.defaults?.baseURL || "").replace(/\/api\/?$/, "");
  const resolveUrl = (u) => (u && u.startsWith("/") ? `${apiBase}${u}` : u);
  const aboutHeading = siteSettings?.about_heading;
  const aboutParagraph = siteSettings?.about_paragraph;

  return (
    <div data-testid="about-page" className="bg-background">
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">About the workshop</span>
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.9]">
              Repco Authorised.<br /><span className="text-brand-red">Glenorchy-built.</span><br />Honestly run.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <div className="relative border border-zinc-900 bg-zinc-950/40 p-4 noise-overlay">
              <img loading="lazy" decoding="async"
                src="/logo.png"
                alt="Tassie Automotive — Repco Authorised Service"
                className="w-full h-auto object-contain brand-pulse"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed">
          {aboutHeading && (
            <h2 data-testid="about-heading" className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white leading-tight">
              {aboutHeading}
            </h2>
          )}
          {aboutParagraph ? (
            <p data-testid="about-paragraph" className="whitespace-pre-line">
              {aboutParagraph}
            </p>
          ) : (
            <>
              <p>
                Tassie Automotive opened its doors at 46 Grove Rd, Glenorchy in 2021 with a simple mission — give
                Tasmanian drivers a workshop they can actually trust. No upsells. No guessing. Just professional
                automotive care, honestly quoted and properly done.
              </p>
              <p>
                Within a few short years we earned <span className="text-white font-semibold">Repco Authorised Service</span>{" "}
                accreditation — meaning every repair we deliver is backed by Australia&apos;s largest aftermarket network and
                their nationwide parts &amp; labour warranty.
              </p>
              <p>
                From general &amp; light vehicle repairs to electrical diagnostics, tyre servicing and balancing — for
                modern daily drivers and classic restorations alike — we treat every car like our own.
              </p>
            </>
          )}

          <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mt-10">
            {[
              { Icon: ShieldCheck, t: "Repco Authorised", d: "Nationwide parts & labour warranty." },
              { Icon: Gauge, t: "Diagnostics", d: "Electrical works & full computer scans." },
              { Icon: Award, t: "Modern & classic", d: "From new EVs to vintage restorations." },
              { Icon: Wrench, t: "Tyres & balance", d: "Sales, fitment, rotation and balancing." },
            ].map((f) => (
              <div key={f.t} className="bg-zinc-950 p-6">
                <f.Icon className="h-5 w-5 text-brand-red" />
                <div className="mt-3 font-display uppercase text-base tracking-tight text-white">{f.t}</div>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-cols-1 gap-4">
          <img loading="lazy" decoding="async" src={INTERIOR} alt="Tassie Automotive — Repco Authorised workshop, Grove Rd Glenorchy" className="w-full h-72 object-cover" />
          <img loading="lazy" decoding="async" src={PRECISION} alt="Tassie Automotive workshop side view with Mt Wellington behind" className="w-full h-72 object-cover" />
        </div>
      </section>

      {gallery.length > 0 && (
        <section data-testid="about-gallery" className="border-t border-zinc-900 bg-black/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-px w-12 bg-brand-red" />
                  <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Inside the workshop</span>
                </div>
                <h2 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                  Bays, lifts &amp; <span className="text-brand-red">the team.</span>
                </h2>
              </div>
              <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-widest">
                <Camera className="h-4 w-4 text-brand-red" /> {gallery.length} {gallery.length === 1 ? "photo" : "photos"}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
              {gallery.map((img, i) => (
                <figure key={img.id} data-testid={`gallery-photo-${img.id}`} className="bg-zinc-950 relative group overflow-hidden">
                  <img decoding="async"
                    src={resolveUrl(img.url)}
                    alt={img.caption || `Tassie Automotive workshop photo ${i + 1}`}
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {img.caption && (
                    <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-4 py-3 text-xs text-zinc-100 leading-snug opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="border-t border-zinc-900">
        <HighlightsWall className="max-w-7xl mx-auto px-6 lg:px-10 py-20" />
      </div>

      <section className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3 className="font-display uppercase text-3xl sm:text-4xl tracking-tighter text-white max-w-xl">Ready to put us to work?</h3>
          <Link to="/book" data-testid="about-book-btn" className="inline-flex items-center gap-3 px-8 py-4 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B]">
            Book your service <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
